/**
 * Per-supplier sub-region inspect view per scope §6.1.2. Atlas Day 2.5 P3
 * follows PerDistributorInspect canonical pattern (Atlas Day 2 P6 commit
 * 3435db2).
 *
 * Substrate currently empty (Phase 0 #2: N=0 suppliers). N=0 conditional
 * framing per D3 lock — eventual-existence framing applied to surfaceLine
 * and L4/L11 content; classification + propagation map shapes carry
 * forward for future supplier population.
 */

import type { JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { PER_SUPPLIER_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function PerSupplierInspect(): JSX.Element {
  const supFields = FIELD_CLASSIFICATIONS.suppliers;
  const fieldEntries = Object.entries(supFields);

  const strategicFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'strategic');
  const mechanicalFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'mechanical');
  const ownershipFields = fieldEntries.filter(([, c]) => c.layer6Authority === 'ownership');
  const operationalFields = fieldEntries.filter(([, c]) => c.layer6Authority === 'operational');
  const naLayer6Fields = fieldEntries.filter(([, c]) => c.layer6Authority === undefined);

  return (
    <InspectShell
      clusterName="Per-entity configuration"
      subRegionName="Per-supplier"
      surfaceLine="ISG has not yet onboarded any suppliers; substrate exists with shapes anticipating future population per scope §6.1.2."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.1.2: lead_time_days + moq = <strong>mechanical</strong> (supplier-reported
          facts, not ISG choices); is_active = <strong>judgment</strong> (operating availability
          toggle).
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Mechanical section ({mechanicalFields.length} fields) — supplier-reported facts
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {mechanicalFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale}` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Strategic section ({strategicFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {strategicFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Operational-altitude ({operationalFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {operationalFields.map(([field]) => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
        {ownershipFields.length > 0 && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-ink-bright mb-1">
              Ownership-altitude ({ownershipFields.length} fields)
            </h3>
            <ul className="list-disc pl-5 text-xs space-y-0.5">
              {ownershipFields.map(([field]) => (
                <li key={field}>
                  <code>{field}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
        {naLayer6Fields.length > 0 && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-ink-bright mb-1">
              Region-altitude ({naLayer6Fields.length} fields, layer 6 N/A per three-state
              applicability) — supplier-reported facts
            </h3>
            <ul className="list-disc pl-5 text-xs space-y-0.5">
              {naLayer6Fields.map(([field]) => (
                <li key={field}>
                  <code>{field}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.1.2 L7: <code>suppliers.provenance</code> sidecar exists (Day 67 P2.2
          7-table coverage); per-row trace activates when supplier substrate populates.
          Current epoch: untraced-by-absence (N=0 rows means no trace events yet).
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker):</p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {PER_SUPPLIER_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.1.2 L9 + classification map: all supplier fields clean-reversible
          (toggle availability + supplier-reported value edits affect future evaluations only;
          no past-decision residue at supplier altitude). Per-field:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Cluster-contribution pattern per scope §6.1.2 L11. Per-supplier configuration
          contributes to ISG&apos;s position via the cluster&apos;s role: structural posture for
          who ISG sources through. At current N=0 substrate state, the contribution is
          architectural (the slot exists, anticipating supplier population at onboarding
          altitude); when populated, per-supplier configurations contribute lead-time +
          moq-aware PO planning to the engine.
        </p>
      </LayerSection>
    </InspectShell>
  );
}
